import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Crown, Users, Wallet, GitBranch, Send, Check, X, Plus, Loader2, MessageCircle } from "lucide-react";
import LineageNode from "../components/LineageNode";

const ROLE_COLOR = {
  anchor: "text-primary",
  initiate: "text-secondary",
  practitioner: "text-foreground/85",
};


export default function TempleHome() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [temple, setTemple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState({ memberOf: [], invitedTo: [] });

  const load = async () => {
    setLoading(true);
    try {
      if (slug) {
        const { data } = await api.get(`/temples/by-slug/${slug}`);
        setTemple(data);
      }
      const { data: m } = await api.get("/temples/me/list");
      setMine(m);
    } catch (e) {
      if (slug) toast.error("Temple not found");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (loading) return <Layout><div className="text-foreground/55 italic">Loading…</div></Layout>;

  if (!slug) return <Layout><TempleListing mine={mine} user={user} onChange={load} /></Layout>;
  if (!temple) return <Layout><div className="parchment p-8">Temple not found.</div></Layout>;

  return (
    <Layout>
      <TempleDetail temple={temple} user={user} onChange={load} />
    </Layout>
  );
}


// ============================================================
// Listing + Create
// ============================================================
function TempleListing({ mine, user, onChange }) {
  const [all, setAll] = useState([]);
  const [name, setName] = useState("");
  const [tradition, setTradition] = useState("");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/temples?limit=20"); setAll(data || []); }
      catch (e) { /* ignore */ }
    })();
  }, []);

  const create = async () => {
    if (name.trim().length < 3) { toast.error("Name a temple"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/temples", { name: name.trim(), tradition: tradition || null });
      toast.success("Temple founded");
      nav(`/temple/${data.slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create");
    } finally { setCreating(false); }
  };

  const accept = async (tid) => {
    try { await api.post(`/temples/${tid}/accept`); toast.success("Joined"); await onChange(); }
    catch (e) { toast.error("Couldn't accept"); }
  };
  const decline = async (tid) => {
    try { await api.post(`/temples/${tid}/decline`); toast.success("Declined"); await onChange(); }
    catch (e) { toast.error("Couldn't decline"); }
  };

  return (
    <div className="space-y-8" data-testid="temple-listing">
      <div>
        <div className="label-mystic">Sacred Houses</div>
        <h1 className="font-serif text-5xl mt-2">Temples</h1>
        <p className="text-foreground/65 mt-3 max-w-2xl">
          A temple lets multiple practitioners share one brand, one inbox-pin, and one
          treasury. Each booking a member completes mirrors a configurable share of CAUL
          to the temple's coffers — funding ads, ceremonies, and apprentices.
        </p>
      </div>

      {/* Invitations */}
      {mine.invitedTo.length > 0 && (
        <div className="parchment p-5 space-y-3" data-testid="temple-invites">
          <div className="label-mystic">Pending invitations</div>
          {mine.invitedTo.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3" data-testid={`invite-${t.id}`}>
              <span className="font-serif text-xl">{t.name}</span>
              <div className="flex gap-2">
                <button onClick={() => accept(t.id)} className="bg-primary text-primary-foreground px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-1" data-testid={`invite-accept-${t.id}`}>
                  <Check size={12}/> Accept
                </button>
                <button onClick={() => decline(t.id)} className="border border-foreground/30 text-foreground/65 px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-1" data-testid={`invite-decline-${t.id}`}>
                  <X size={12}/> Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My temples */}
      {mine.memberOf.length > 0 && (
        <div className="space-y-2">
          <div className="label-mystic">My temples</div>
          {mine.memberOf.map((t) => (
            <a key={t.id} href={`/temple/${t.slug}`} className="parchment p-4 flex items-center justify-between hover:border-primary/40 transition-colors block" data-testid={`my-temple-${t.id}`}>
              <span className="font-serif text-xl">{t.name}</span>
              <span className={`text-xs uppercase tracking-wider ${ROLE_COLOR[t.role] || "text-foreground/65"}`}>{t.role}</span>
            </a>
          ))}
        </div>
      )}

      {/* Found a temple (providers only, no existing anchor) */}
      {user?.role === "PROVIDER" && !mine.memberOf.some((t) => t.role === "anchor") && (
        <div className="parchment p-6 space-y-4" data-testid="temple-create-card">
          <div>
            <div className="label-mystic">Found a temple</div>
            <h2 className="font-serif text-2xl mt-1">You may anchor your own</h2>
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Temple name"
            className="w-full bg-background border border-secondary/30 px-4 py-3 text-foreground rounded-sm placeholder:text-foreground/35"
            data-testid="temple-name-input" />
          <input value={tradition} onChange={(e) => setTradition(e.target.value)}
            placeholder="Tradition (optional, e.g. Yoruba Ifá)"
            className="w-full bg-background border border-secondary/30 px-4 py-3 text-foreground rounded-sm placeholder:text-foreground/35"
            data-testid="temple-tradition-input" />
          <button onClick={create} disabled={creating}
            className="bg-primary text-primary-foreground px-6 py-2.5 uppercase tracking-[0.2em] text-xs rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="temple-create-btn">
            {creating ? <><Loader2 size={12} className="animate-spin"/> Founding…</> : <><Plus size={12}/> Found temple</>}
          </button>
        </div>
      )}

      {/* All temples */}
      <div className="space-y-2">
        <div className="label-mystic">All temples</div>
        {all.map((t) => (
          <a key={t.id} href={`/temple/${t.slug}`} className="parchment p-4 flex items-center justify-between hover:border-primary/40 transition-colors block" data-testid={`temple-row-${t.slug}`}>
            <div>
              <div className="font-serif text-xl">{t.name}</div>
              <div className="text-xs text-foreground/55">{t.tradition || "—"} · {t.memberCount} member{t.memberCount === 1 ? "" : "s"}</div>
            </div>
            <div className="text-xs text-secondary uppercase tracking-wider">{t.treasuryBalance.toLocaleString()} CAUL</div>
          </a>
        ))}
        {all.length === 0 && <div className="text-foreground/45 italic">No temples founded yet.</div>}
      </div>
    </div>
  );
}


// ============================================================
// Detail view
// ============================================================
function TempleDetail({ temple, user, onChange }) {
  const isAnchor = temple.anchorUserId === user?.id;
  const isMember = temple.members.some((m) => m.userId === user?.id);
  const [inviteUserId, setInviteUserId] = useState("");
  const [inviteRole, setInviteRole] = useState("practitioner");
  const [inviteShare, setInviteShare] = useState(5);
  const [ledger, setLedger] = useState(null);
  const [lineage, setLineage] = useState(null);

  useEffect(() => {
    if (isMember) {
      api.get(`/temples/${temple.id}/treasury/ledger?limit=20`)
        .then(({ data }) => setLedger(data)).catch(() => {});
    }
    api.get(`/lineage/tree/${temple.anchorUserId}?depth=4`)
      .then(({ data }) => setLineage(data)).catch(() => {});
  }, [temple.id, isMember, temple.anchorUserId]);

  const sendInvite = async () => {
    if (!inviteUserId) { toast.error("User ID required"); return; }
    try {
      await api.post(`/temples/${temple.id}/invite`, {
        userId: inviteUserId, role: inviteRole, revenueSharePct: parseFloat(inviteShare),
      });
      toast.success("Invitation sent");
      setInviteUserId("");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't invite");
    }
  };

  const leave = async () => {
    if (!window.confirm("Leave this temple?")) return;
    try { await api.post(`/temples/${temple.id}/leave`); toast.success("Left"); await onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't leave"); }
  };

  return (
    <div className="space-y-8" data-testid="temple-detail">
      <div className="parchment p-8">
        <div className="label-mystic">Temple · {temple.tradition || "Open Lineage"}</div>
        <h1 className="font-serif text-5xl mt-2">{temple.name}</h1>
        {temple.lineageStatement && (
          <p className="text-foreground/75 mt-4 max-w-3xl italic leading-relaxed">{temple.lineageStatement}</p>
        )}
        <div className="flex items-center gap-6 mt-6 flex-wrap">
          <Stat label="Members" value={temple.members.length} icon={Users} />
          <Stat label="Treasury" value={`${(temple.treasuryBalance || 0).toLocaleString()} CAUL`} icon={Wallet} color="text-secondary" />
        </div>
        {isMember && !isAnchor && (
          <button onClick={leave} className="mt-6 border border-destructive/50 text-destructive px-4 py-2 text-xs uppercase tracking-wider rounded-sm" data-testid="temple-leave-btn">
            Leave temple
          </button>
        )}
      </div>

      {/* Roster */}
      <div>
        <div className="label-mystic mb-3">Roster</div>
        <div className="space-y-2" data-testid="temple-roster">
          {temple.members.map((m) => (
            <div key={m.userId} className="parchment p-4 flex items-center justify-between" data-testid={`roster-${m.userId}`}>
              <div className="flex items-center gap-3">
                {m.role === "anchor" && <Crown size={14} className="text-primary" />}
                <a href={`/profile/${m.userId}`} className="font-serif text-lg hover:text-secondary">
                  {m.user?.name || m.userId}
                </a>
                <span className={`text-xs uppercase tracking-wider ${ROLE_COLOR[m.role]}`}>{m.role}</span>
              </div>
              {m.revenueSharePct > 0 && (
                <span className="text-xs text-secondary">{m.revenueSharePct}% treasury share</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Invite — anchor only */}
      {isAnchor && (
        <div className="parchment p-6 space-y-3" data-testid="temple-invite-form">
          <div className="label-mystic">Invite a practitioner</div>
          <input value={inviteUserId} onChange={(e) => setInviteUserId(e.target.value)}
            placeholder="User ID to invite"
            className="w-full bg-background border border-secondary/30 px-4 py-2 text-foreground rounded-sm placeholder:text-foreground/35"
            data-testid="invite-userid-input" />
          <div className="flex gap-3">
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
              className="bg-background border border-secondary/30 px-3 py-2 text-foreground rounded-sm"
              data-testid="invite-role-select">
              <option value="practitioner">Practitioner</option>
              <option value="initiate">Initiate</option>
            </select>
            <input type="number" min="0" max="25" step="0.5" value={inviteShare}
              onChange={(e) => setInviteShare(e.target.value)}
              className="bg-background border border-secondary/30 px-3 py-2 text-foreground rounded-sm w-28"
              data-testid="invite-share-input" />
            <span className="text-xs text-foreground/55 self-center">% treasury share (0–25)</span>
          </div>
          <button onClick={sendInvite}
            className="bg-primary text-primary-foreground px-5 py-2 uppercase tracking-[0.2em] text-xs rounded-sm hover:bg-primary/90 inline-flex items-center gap-2"
            data-testid="invite-send-btn">
            <Send size={12}/> Send invitation
          </button>
        </div>
      )}

      {/* Treasury ledger — members only */}
      {ledger && (
        <div data-testid="treasury-ledger">
          <div className="label-mystic mb-3 flex items-center gap-2"><Wallet size={12}/> Treasury ledger</div>
          {ledger.entries.length === 0 ? (
            <div className="text-foreground/45 italic">No contributions yet. Completed bookings will mirror here.</div>
          ) : (
            ledger.entries.map((e) => (
              <div key={e.id} className="parchment p-3 flex items-center justify-between text-sm" data-testid={`ledger-${e.id}`}>
                <span className="text-foreground/65">{new Date(e.createdAt).toLocaleString()}</span>
                <span className="text-foreground/55">booking · {e.memberShare}% share</span>
                <span className="text-secondary font-mono">+{e.amount} CAUL</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Lineage tree */}
      {lineage && (
        <div data-testid="lineage-tree">
          <div className="label-mystic mb-3 flex items-center gap-2"><GitBranch size={12}/> Lineage tree</div>
          <div className="parchment p-6">
            <LineageNode node={lineage} depth={0} />
          </div>
        </div>
      )}

      {/* Shared inbox — members only */}
      {isMember && <TempleInbox templeId={temple.id} />}
    </div>
  );
}

function TempleInbox({ templeId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/temples/${templeId}/inbox?limit=50`);
      setMessages(data.messages || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [templeId]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/temples/${templeId}/inbox`, { text: text.trim() });
      setText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="temple-inbox" id="inbox">
      <div className="label-mystic mb-3 flex items-center gap-2"><MessageCircle size={12}/> Shared inbox</div>
      <div className="parchment p-4 space-y-3">
        <div className="max-h-72 overflow-y-auto space-y-2" data-testid="inbox-messages">
          {messages.length === 0 && (
            <div className="text-foreground/45 italic text-sm">No messages yet. Open the circle.</div>
          )}
          {messages.map((m) => (
            <div key={m.id} className="text-sm flex gap-2" data-testid={`inbox-msg-${m.id}`}>
              <span className="text-secondary font-serif min-w-[120px]">{m.fromName}</span>
              <span className="text-foreground/85 flex-1">{m.text}</span>
              <span className="text-[10px] text-foreground/40 whitespace-nowrap">{new Date(m.createdAt).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-secondary/15">
          <input value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Message the temple…"
            maxLength={4000}
            className="flex-1 bg-background border border-secondary/30 px-3 py-2 text-foreground rounded-sm placeholder:text-foreground/35 text-sm"
            data-testid="inbox-input" />
          <button onClick={send} disabled={sending || !text.trim()}
            className="bg-primary text-primary-foreground px-4 py-2 uppercase tracking-wider text-xs rounded-sm disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="inbox-send-btn">
            {sending ? <Loader2 size={11} className="animate-spin"/> : <Send size={11}/>} Send
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color = "text-foreground/85" }) {
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-foreground/55" />}
      <span className="text-xs text-foreground/55 uppercase tracking-wider">{label}</span>
      <span className={`font-serif text-xl ${color}`}>{value}</span>
    </div>
  );
}
